import { useEffect, useRef, useState } from 'react';

// Template documents, fetched once per template version and shared by every card and
// preview. A summary without preview images renders from its document instead, fetched
// when its card scrolls into view.
const cache = new Map();
export const templateKey = template => `${template.id}@${template.timestamps?.updated || ''}`;

export function loadTemplate(template, fetch) {
  const key = templateKey(template);
  if (!cache.has(key)) cache.set(key, fetch(template.id).catch(failure => { cache.delete(key); throw failure; }));
  return cache.get(key);
}

export default function useTemplateDocument(template, fetch, { eager = false } = {}) {
  const ref = useRef(null);
  const [state, setState] = useState({ status: 'idle', document: null });
  const [visible, setVisible] = useState(eager);
  useEffect(() => {
    if (eager || visible || !ref.current) return undefined;
    const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) setVisible(true); }, { rootMargin: '200px' });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [eager, visible]);
  useEffect(() => {
    if (!visible || !fetch || !template) return undefined;
    let alive = true;
    setState(previous => (previous.document ? previous : { status: 'loading', document: null }));
    loadTemplate(template, fetch).then(detail => alive && setState({ status: 'ready', document: detail.document }))
      .catch(() => alive && setState({ status: 'error', document: null }));
    return () => { alive = false; };
  }, [visible, fetch, template]);
  return { ref, ...state };
}
